/**
 * PasswordRequirements — live checklist shown under any "set a password" field.
 *
 * MUST stay in sync with functions/_lib/password.ts (passwordChecks). The server
 * is the enforcer; this only tells the user what's missing before they submit.
 */
import { Check, X } from "lucide-react";

export const PASSWORD_MIN_LENGTH = 8;
const SPECIAL_RE = /[!@#$%^&*()\-_=+[\]{};:,.<>?/\\|`~"']/;
const COMMON_FRAGMENTS = [
  "password", "passw0rd", "12345678", "qwerty", "letmein", "welcome",
  "iloveyou", "admin123", "noexcuses", "disputes", "creditrepair", "changeme",
];

export interface PwCheck { id: string; label: string; ok: boolean }

export function passwordChecks(pw: string): PwCheck[] {
  const p = pw || "";
  return [
    { id: "length", label: `At least ${PASSWORD_MIN_LENGTH} characters`, ok: p.length >= PASSWORD_MIN_LENGTH },
    { id: "uppercase", label: "An uppercase letter (A–Z)", ok: /[A-Z]/.test(p) },
    { id: "lowercase", label: "A lowercase letter (a–z)", ok: /[a-z]/.test(p) },
    { id: "number", label: "A number (0–9)", ok: /[0-9]/.test(p) },
    { id: "special", label: "A special character (!@#$%…)", ok: SPECIAL_RE.test(p) },
    { id: "not_common", label: "No common or guessable words", ok: !!p && !COMMON_FRAGMENTS.some((c) => p.toLowerCase().includes(c)) },
  ];
}

/** True when every rule passes — use to gate the submit button. */
export function passwordOk(pw: string): boolean {
  return passwordChecks(pw).every((c) => c.ok);
}

export function PasswordRequirements({ password, className }: { password: string; className?: string }) {
  const checks = passwordChecks(password);
  const met = checks.filter((c) => c.ok).length;
  return (
    <div className={className} style={{ margin: "8px 0 2px" }} aria-live="polite">
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }} aria-hidden="true">
        {checks.map((c) => (
          <span
            key={c.id}
            style={{
              height: 3, flex: 1, borderRadius: 2,
              background: c.ok ? "var(--ne-success, #2ea043)" : "var(--ne-silver-1, #444)",
              transition: "background 140ms ease",
            }}
          />
        ))}
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 3 }}>
        {checks.map((c) => (
          <li key={c.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: c.ok ? "var(--ne-success, #2ea043)" : "var(--ne-muted, #8b8b93)" }}>
            {c.ok
              ? <Check size={13} aria-hidden="true" />
              : <X size={13} aria-hidden="true" style={{ opacity: 0.55 }} />}
            <span>{c.label}</span>
          </li>
        ))}
      </ul>
      <span className="sr-only">{met} of {checks.length} password requirements met</span>
    </div>
  );
}
